import { calculateFinanceXP } from './ecologicalXP';

/**
 * Finance economy XP (EC3) — a DERIVED view of how much you use the personal
 * finance system, credited to the Ecological domain + the Financial Stewardship
 * area. "Using the system regularly grants XP": each logged transaction, created
 * budget, and savings goal is worth its existing FINANCE_XP value.
 *
 * This is the SINGLE source both `useUserStats` (domain credit, once) and
 * `useDisciplines` (the Financial Stewardship area rank, a view) read — so the
 * domain is credited exactly once and the area never re-mints. Replaces the old
 * `awardFinanceXP → ecologicalAreasStore` path (the retired parallel ledger).
 */
export interface FinanceCounts {
  transactions: number;
  budgets: number;
  goals: number;
}

/**
 * Total finance economy XP from the current finance data. Pure + idempotent.
 * Values are read per call (NOT captured at module init) so the XP-economy
 * config hydration, which runs after import time, is always respected.
 */
export function computeFinanceEconomyXP(counts: FinanceCounts): number {
  const transactionXP = calculateFinanceXP({ type: 'transaction_logged' }); // default 5
  const budgetXP = calculateFinanceXP({ type: 'budget_created' }); // default 20
  const goalXP = calculateFinanceXP({ type: 'budget_created' }); // a savings goal awards budget_created
  const t = Math.max(0, counts.transactions || 0);
  const b = Math.max(0, counts.budgets || 0);
  const g = Math.max(0, counts.goals || 0);
  return t * transactionXP + b * budgetXP + g * goalXP;
}
